import { booleanAttribute, ChangeDetectionStrategy, Component, ElementRef, inject, input } from '@angular/core';

export type IconButtonVariant = 'plain' | 'soft' | 'floating';

/**
 * Botón de sólo ícono. `label` es obligatorio y es su nombre accesible ("Regresar", "Notificaciones"…).
 * `pressed` lo vuelve un toggle con `aria-pressed`; sin él no se publica el atributo. El área es de 44×44
 * aunque el dibujo sea menor.
 *
 * El nombre es texto oculto, no `aria-label`: el WebView de Android descarta el `aria-label` de un toggle
 * y TalkBack sólo decía "desactivado". axe no lo detecta; se vio en el árbol que Android le da a TalkBack.
 */
@Component({
  selector: 'button[uiIconButton]',
  template: `
    <span class="ui-visually-hidden">{{ label() }}</span>
    <ng-content />
    @if (dot()) {
      <span class="dot" aria-hidden="true"></span>
    }
  `,
  styleUrl: './icon-button.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.aria-pressed]': 'pressed() === undefined ? null : pressed()',
    '[class.variant-plain]': 'variant() === "plain"',
    '[class.variant-soft]': 'variant() === "soft"',
    '[class.variant-floating]': 'variant() === "floating"',
    '[class.active]': 'active() || pressed() === true',
  },
})
export class IconButtonComponent {
  readonly label = input.required<string>();
  readonly variant = input<IconButtonVariant>('plain');
  readonly pressed = input<boolean>();
  readonly active = input(false, { transform: booleanAttribute });
  /** Punto de "no leído". Quien lo usa también lo dice en `label`: un punto no se lee. */
  readonly dot = input(false, { transform: booleanAttribute });

  constructor() {
    const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    if (!host.hasAttribute('type')) host.setAttribute('type', 'button');
  }
}
